import { subnavPaneContentsAtom, subnavPaneVerticalOffsetAtom } from 'components/sidebar/lib/atoms'
import { navItemIsActive } from 'components/sidebar/lib/navItems'
import { AnimatePresence } from 'motion/react'
import { motion } from 'motion/react'
import { useAtomValue } from 'jotai'
import { FC, c } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import { usePrevious } from 'lib/hooks'
import { useIntl } from 'lib/intl-utils'
import Link from 'components/base/Link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { P, match } from 'ts-pattern'
import { clamp } from 'lodash'

type Props = {}

const SubnavPane: FC<Props> = ({ className, style }) => {
  const ctx = useUserContext()
  const { t } = useIntl()
  const subnavItems = useAtomValue(subnavPaneContentsAtom)
  const subnavPaneVerticalOffset = useAtomValue(subnavPaneVerticalOffsetAtom)
  const previousSubnavItems = usePrevious(subnavItems)
  const [screenHeight, setScreenHeight] = useState(0)
  const router = useRouter()

  useEffect(() => {
    setScreenHeight(window.innerHeight - 8)
  }, [])

  const itemHeight = match(subnavItems)
    .with(null, () => 0)
    .with([{ name: 'settings' }, ...P.array()], () => 534)
    .with([{ name: 'language' }, ...P.array()], () => 248)
    .with([{ name: 'change_mall' }, ...P.array()], () => ctx.malls.length * 45 + 36 + 32)
    .otherwise(() => (subnavItems!.length - 1) * 45 + 36 + 32)

  return (
    <AnimatePresence>
      {subnavItems && subnavItems.length > 2 && (
        <motion.div
          className={c`w-[236px] h-screen bg-white/90 backdrop-blur-md backdrop-saturate-200 rounded-r-xl border-x border-gray-200 ${className}`}
          initial={{ opacity: 0, translateX: -48 }}
          animate={{ opacity: 1, translateX: 0 }}
          exit={{ opacity: 0, translateX: -48 }}
          transition={{ ease: 'easeInOut', duration: 0.15 }}
          style={style}
        >
          <AnimatePresence>
            <motion.div
              key={subnavItems.map((item) => item.name).join()}
              initial={{
                opacity: 0,
                translateY: clamp(
                  subnavPaneVerticalOffset - itemHeight / 2,
                  0,
                  screenHeight - itemHeight
                ),
              }}
              animate={{
                opacity: 1,
                transition: { ease: 'easeOut', delay: previousSubnavItems ? 0.35 : 0 },
                translateY: clamp(
                  subnavPaneVerticalOffset - itemHeight / 2,
                  0,
                  screenHeight - itemHeight
                ),
              }}
              exit={{
                opacity: 0,
                transition: { ease: 'easeIn' },
                translateY: clamp(
                  subnavPaneVerticalOffset - itemHeight / 2,
                  0,
                  screenHeight - itemHeight
                ),
              }}
              transition={{ duration: 0.0 }}
              className="flex flex-col py-4"
            >
              <div className="px-[18px] py-1.5">
                <div className="w-fit text-xs font-medium text-gray-700 ring-1 ring-gray-200 rounded-md px-2 py-1">
                  {t(subnavItems[0].name)}
                </div>
              </div>
              {subnavItems.slice(1).map((item) =>
                item.customRender ? (
                  item.customRender(ctx, t, item)
                ) : (
                  <Link
                    key={item.name}
                    href={item.route + (item.addMall ? `${ctx.currentMall.code}/` : '')}
                    className={c`group px-2 py-0.5 font-medium text-gray-700`}
                  >
                    <div
                      className={c`py-2.5 px-3 rounded-lg flex items-center gap-2.5 ${navItemIsActive(
                        item,
                        router
                      )} bg-primary-100 group-hover:bg-primary-200 | group-hover:bg-gray-100`}
                    >
                      {item.icon && <item.icon className="size-[18px] text-gray-700" />}
                      <span>{t(item.translationKey ?? item.name)}</span>
                    </div>
                  </Link>
                )
              )}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default SubnavPane
